import { supabase } from "@/integrations/supabase/client";

// ============================================================
// ROOT Rewards Network — client service layer
// ROOT is the participation and rewards unit of the Sugar Town
// Organics ecosystem. This is a ledger-based rewards system.
// ============================================================

export interface RootAccount {
  id: string;
  user_id: string | null;
  customer_email: string | null;
  current_balance: number;
  lifetime_earned: number;
  lifetime_redeemed: number;
  status: string;
  external_wallet_address: string | null;
  created_at: string;
  updated_at: string;
}

export interface RootLedgerEntry {
  id: string;
  root_account_id: string;
  delta: number;
  balance_after: number;
  entry_type: string;
  source_type: string | null;
  source_id: string | null;
  description: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface RootReward {
  id: string;
  title: string;
  description: string | null;
  roots_cost: number;
  reward_type: string;
  redemption_instructions: string | null;
  quantity_available: number | null;
  quantity_redeemed: number;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  metadata: Record<string, unknown>;
}

export interface RootRedemption {
  id: string;
  reward_id: string;
  roots_spent: number;
  status: string;
  redemption_code: string | null;
  notes: string | null;
  created_at: string;
  fulfilled_at: string | null;
}

export interface RootRule {
  id: string;
  name: string;
  slug: string;
  action_type: string;
  reward_amount: number;
  is_active: boolean;
  conditions: Record<string, unknown>;
  max_awards_per_user: number | null;
  cooldown_hours: number | null;
}

/** Default earn rate fallback: 1 ROOT per XCD $1 spent. */
export const DEFAULT_EARN_RATE_PER_XCD = 1;

/** Fetch the global ROOT earn rate (ROOT per XCD $1). */
export async function getEarnRate(): Promise<number> {
  // root_settings is no longer publicly readable; only the earn rate is
  // exposed (to anon + authenticated) via a security-definer RPC.
  const { data } = await supabase.rpc("get_root_earn_rate");
  const rate = Number(data);
  return Number.isFinite(rate) && rate > 0 ? rate : DEFAULT_EARN_RATE_PER_XCD;
}

/** Estimate ROOT earned for a given XCD amount using the configured rate. */
export function estimateRootForAmount(amountXcd: number, rate = DEFAULT_EARN_RATE_PER_XCD): number {
  if (!Number.isFinite(amountXcd) || amountXcd <= 0) return 0;
  return Math.floor(amountXcd * rate);
}

/** Ensure the signed-in member has a ROOT account and return it. */
export async function ensureMyRootAccount(): Promise<RootAccount | null> {
  const { data, error } = await supabase.rpc("ensure_my_root_account");
  if (error) {
    console.error("ensure_my_root_account failed", error);
    return null;
  }
  return data as unknown as RootAccount;
}

/** Get the signed-in member's current ROOT balance. */
export async function getRootBalance(): Promise<number> {
  const account = await ensureMyRootAccount();
  return account?.current_balance ?? 0;
}

/** Get the signed-in member's recent ledger activity. */
export async function getRootLedger(limit = 25): Promise<RootLedgerEntry[]> {
  const { data, error } = await supabase
    .from("root_ledger")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) {
    console.error("getRootLedger failed", error);
    return [];
  }
  return (data ?? []) as unknown as RootLedgerEntry[];
}

/** Get the signed-in member's redemptions. */
export async function getMyRedemptions(): Promise<RootRedemption[]> {
  const { data, error } = await supabase
    .from("root_redemptions")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) {
    console.error("getMyRedemptions failed", error);
    return [];
  }
  return (data ?? []) as unknown as RootRedemption[];
}

/** List active rewards available to redeem. */
export async function getActiveRewards(): Promise<RootReward[]> {
  const { data, error } = await supabase
    .from("root_rewards")
    .select("*")
    .eq("is_active", true)
    .order("roots_cost", { ascending: true });
  if (error) {
    console.error("getActiveRewards failed", error);
    return [];
  }
  return (data ?? []) as unknown as RootReward[];
}

/** List active earning rules (for the "how to earn" displays). */
export async function getActiveRules(): Promise<RootRule[]> {
  const { data, error } = await supabase
    .from("root_rules")
    .select("*")
    .eq("is_active", true)
    .order("reward_amount", { ascending: false });
  if (error) {
    console.error("getActiveRules failed", error);
    return [];
  }
  return (data ?? []) as unknown as RootRule[];
}

/** Redeem a reward for the signed-in member. */
export async function redeemReward(rewardId: string): Promise<{
  ok: boolean;
  code?: string;
  balance?: number;
  error?: string;
}> {
  const { data, error } = await supabase.rpc("redeem_my_roots", { p_reward_id: rewardId });
  if (error) {
    return { ok: false, error: error.message };
  }
  const result = data as { redemption_code?: string; balance?: number };
  return { ok: true, code: result?.redemption_code, balance: result?.balance };
}

/**
 * Award ROOT for a completed order/booking via the secure edge function.
 * Duplicate awards for the same source are prevented server-side.
 */
export async function awardRootsForSource(params: {
  email: string;
  amount: number;
  entryType: string;
  sourceType: string;
  sourceId: string;
  description?: string;
  metadata?: Record<string, unknown>;
}): Promise<{ ok: boolean; awarded?: number; duplicate?: boolean; balance?: number }> {
  const { data, error } = await supabase.functions.invoke("award-roots", {
    body: params,
  });
  if (error) {
    console.error("award-roots failed", error);
    return { ok: false };
  }
  return { ok: true, ...(data as object) };
}
